import { useRef, useState } from 'react'
import type { ReactNode, RefObject } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Alert, Badge, Button, Code, Group, List, Paper, SegmentedControl, SimpleGrid, Stack, Table, Text,
  Skeleton as MSkeleton,
} from '@mantine/core'
import {
  IconAlertOctagon, IconAlertTriangle, IconCalendarOff, IconCircleX, IconClockQuestion, IconEye,
  IconInfoCircle, IconRefresh, IconServerOff,
} from '@tabler/icons-react'
import {
  EmptyState, HairlineDivider, MetaList, PageTitle, SectionAction, SectionCard, StatTile,
} from '@lighthouse/design'
import { BucketDetail } from '../components/schedules/BucketDetail'
import { LoadTimeline, TimelineLegend } from '../components/schedules/LoadTimeline'
import { QuarterHourTable } from '../components/schedules/QuarterHourTable'
import { SeriesTable } from '../components/schedules/SeriesTable'
import { TownCrierLink } from '../components/schedules/TownCrierLink'
import { VerdictBadge } from '../components/schedules/VerdictBadge'
import { RestrictedState } from '../components/access/RestrictedState'
import {
  DEFAULT_HOURS, VERDICT_ORDER, WINDOW_OPTIONS, atLabel, bannerColor, browserZone, countLabel, dayLabel,
  durationLabel, firstListedBySeries, humanizeCode, inFlightKindLabel, kindLabel, offsetLabel, parseHours,
  plural, ratioPercent, sameInstant, slotSecondsLabel, splitBanners, verdictMeaning, wallClock,
  windowLabel, workerZoneExample, workerZoneProblemText,
} from '../components/schedules/scheduleFormat'
import { useScheduleLoad } from '../hooks/useOverview'
import { useEtlJobCatalog } from '../hooks/useEtlJobs'
import { useLighthouseAdmin } from '../hooks/useLighthouseAdmin'
import { isApiError } from '../services/apiError'
import { NOT_MEASURED, agoLabel, errorKind, errorSummary, exactNumber, whenLabel } from '../utils/format'
import type { ErrorKind } from '../utils/format'
import type {
  ScheduleLoad, ScheduleLoadBanner, ScheduleLoadBucket, ScheduleLoadCapacity, ScheduleLoadDisplayZone,
  ScheduleLoadWorkerZone,
} from '../types/scheduleLoad'

const PAGE_SUBTITLE =
  'What is scheduled to run, when, and whether the one shared worker pool can take it — a forecast from now'

/**
 * Schedules — *Tide Tables*: the load forecast (build plan #8, doc 11).
 *
 * The question, in the user's words: "I have 200 reports scheduled for execution
 * at 3pm. What those reports are, and will this break stuff." Reports and ETL
 * contend for ONE Hangfire worker pool — the worker listens to etl, reports and
 * default with no ORDER BY — and only Lighthouse can see both sides of it.
 * Cartographer owns the schedule; Observatory owns the consequence (doc 11 §1).
 *
 * `GET api/observatory/schedule-load` computes everything. This page renders it,
 * and recomputes none of it — see `scheduleFormat.ts` for the rule and the one
 * place the browser's zone database is consulted (a label, never a placement).
 *
 * The three decisions, and how the page keeps them:
 *
 *  - **S2 — opens on the forecast.** The verdict block leads, the timeline is
 *    directly under it, and the quarter-hour selected on arrival is the server's
 *    `worstVerdictFirstAt`, so the first thing opened is the first thing to fear.
 *  - **S3 — the verdict AND every number behind it.** This surface predicts, and
 *    predictions are wrong in ways measurements are not, so nothing is only a
 *    conclusion. The window's `scope`, `notModelled` and `assumptions` sit IN the
 *    verdict block, not in a footnote: "Clear" means clear for scheduled demand
 *    only. Each quarter-hour's starts, unknowns, demand, occupancy, queue and wait
 *    are table columns; each occurrence's cost opens onto its samples and
 *    percentiles. Nothing load-bearing lives only in a tooltip.
 *  - **S1 — read-only.** No field edits anything. The window selector and the
 *    table filters change what is SHOWN; the only act is the hand-off to Town
 *    Crier, which is a link when this build knows where Cartographer is and a
 *    named destination when it does not — never a link to nowhere.
 *
 * And the traps doc 11 §6 names, each met where it bites:
 *
 *  - **§6.1, the zone.** The browser's IANA zone is sent and never substituted;
 *    a browser that reports none gets a page that says so. Every wall clock is
 *    read from the server's per-instant offset, and every timeline row and table
 *    row states its offset, so a window across a daylight-saving change is right
 *    on both sides of it. Automaton's own scheduling zone is shown beside the
 *    timeline — in production it is UTC, which puts the 02:30 maintenance chain
 *    in a Pacific evening, and the page shows that with a real job from the
 *    window rather than asserting it.
 *  - **§6.2, slot-seconds.** The demand columns are slot-seconds and average
 *    slots wanted, not counts. "200 starting" is shown, and never alone.
 *  - **§6.3, unknown is unknown.** An unestimated occurrence reads "Unknown" in
 *    words and an outline, never 0 and never a dash; its count sits beside every
 *    start count; demand figures that leave unknowns out say they are a floor.
 *  - **§6.4, truncation.** A cut-short series makes the forecast a floor, and the
 *    headline says so beside the worst verdict, not only in a banner.
 *  - **Unmeasured capacity is not zero slots.** Every ratio is a dash then, the
 *    timeline cells are empty dashed outlines, and the verdict is Unmeasured.
 *
 * `isLoading || !data` guards the body (build plan #3): between React Query's
 * retries the flags read "loaded" with no payload, and on this page a missing
 * payload rendered through defaults would be a forecast of nothing — a calm
 * page, which is the most dangerous thing this surface can show.
 *
 * **The gate.** The controller is `LighthouseAdmin` at class level. When the
 * server has already said this caller lacks the role (the job catalogue's
 * permission, via `useLighthouseAdmin`), the forecast is NOT requested at all:
 * `useScheduleLoad` has no `enabled` option to hold it back and a hook cannot be
 * called conditionally, so this outer component decides and the fetching body is
 * simply never mounted. While the catalogue has not answered yet, the page
 * waits for it: mounting the body then would request the forecast for a caller
 * the answer is about to refuse (the browser check caught exactly that — one
 * forecast request per visit from a denied caller). It is one shared, cached
 * query the rail has usually loaded already, so the wait is normally none. When
 * the gate has answered and cannot say (`unknown`: the catalogue failed, or
 * predates the field), the body mounts and a 403 of its own renders the same
 * restricted state. An administrator is never locked out by a missing answer.
 */
export function ScheduleLoadPage() {
  const gate = useLighthouseAdmin()
  // The same query the gate reads (one key, one request), asked only whether it
  // has answered yet.
  const { isPending } = useEtlJobCatalog()
  if (gate.state === 'denied') return <RestrictedPage reason={gate.reason} />
  if (isPending) return <GatePending />
  return <ScheduleLoadBody />
}

/** The catalogue has not said yet whether this caller may read the forecast. */
function GatePending() {
  return (
    <Stack gap="lg" pb="xl">
      <PageTitle subtitle={PAGE_SUBTITLE}>Schedules</PageTitle>
      <Stack gap="md">
        <MSkeleton height={260} />
        <MSkeleton height={220} />
      </Stack>
    </Stack>
  )
}

/** The page for a caller the server does not serve this surface to: no forecast, no window controls. */
function RestrictedPage({ reason }: { reason?: string | null }) {
  return (
    <Stack gap="lg" pb="xl">
      <PageTitle subtitle={PAGE_SUBTITLE}>Schedules</PageTitle>
      <RestrictedState surface="Schedules" reason={reason} />
    </Stack>
  )
}

function ScheduleLoadBody() {
  // Read once. The zone is the reader's, not a setting: changing it mid-session
  // would be the browser's decision, and a reload picks it up.
  const [zone] = useState(browserZone)
  const [searchParams, setSearchParams] = useSearchParams()
  const hours = parseHours(searchParams.get('hours'))
  const [selectedUtc, setSelectedUtc] = useState<string | null>(null)
  const detailRef = useRef<HTMLDivElement>(null)

  const { data, isLoading, isError, error, isFetching, refetch } = useScheduleLoad(zone, hours)

  const setHours = (next: number) => {
    // A selected quarter-hour belongs to the old window's response.
    setSelectedUtc(null)
    // In the URL, so the view is a link somebody can send.
    setSearchParams(next === DEFAULT_HOURS ? {} : { hours: String(next) }, { replace: true })
  }

  // A 403 is a missing role, not a fault: the same restricted state the gate
  // shows, even over a forecast already on screen, because the server has now
  // said this caller may not read it.
  if (isError && errorKind(error) === 'forbidden') {
    return <RestrictedPage reason={isApiError(error) ? error.detail : null} />
  }

  return (
    <Stack gap="lg" pb="xl">
      <PageTitle subtitle={PAGE_SUBTITLE}>Schedules</PageTitle>

      <Paper p="md" withBorder style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <Stack gap="sm">
          <Group justify="space-between" align="flex-end">
            <Stack gap={4}>
              <Text size="xs" c="dimmed">Window, from the current quarter-hour</Text>
              <SegmentedControl
                value={String(hours)}
                onChange={(value) => setHours(parseHours(value))}
                data={WINDOW_OPTIONS.map((h) => ({ value: String(h), label: windowLabel(h) }))}
                disabled={zone == null}
              />
            </Stack>
            <Group gap="sm" align="center">
              <Text size="xs" c="dimmed">
                {data ? `Forecast made ${agoLabel(data.generatedAt)}` : ''}
              </Text>
              {/*
                Explicit, because the poll is slow on purpose (see useScheduleLoad):
                an administrator who has just moved a schedule in Town Crier wants
                the new forecast now, not in two minutes.
              */}
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                onClick={() => void refetch()}
                loading={isFetching}
                disabled={zone == null}
              >
                Refresh
              </Button>
            </Group>
          </Group>
          {data && <HandOff path={data.townCrier.path} limitation={data.townCrier.limitation} />}
        </Stack>
      </Paper>

      {zone == null ? (
        <NoBrowserZone />
      ) : isError && !data ? (
        <LoadError error={error} />
      ) : isLoading || !data ? (
        <Stack gap="md">
          <MSkeleton height={260} />
          <MSkeleton height={220} />
          <MSkeleton height={420} />
        </Stack>
      ) : (
        <>
          {/*
            A failed POLL keeps the last good forecast on screen, but says so: the
            page must not present a forecast it could not refresh as current.
          */}
          {isError && (
            <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light" title="The last refresh failed">
              <Text size="sm">
                Showing the forecast made {agoLabel(data.generatedAt)}. {errorSummary(error) ?? ''}
              </Text>
            </Alert>
          )}
          <Forecast
            data={data}
            selectedUtc={selectedUtc}
            onSelect={setSelectedUtc}
            detailRef={detailRef}
          />
        </>
      )}
    </Stack>
  )
}

/** Read-only, said once, with the only act this page has. */
function HandOff({ path, limitation }: { path: string; limitation: string }) {
  return (
    <>
      <HairlineDivider />
      <Group gap="sm" align="flex-start" wrap="nowrap">
        <IconEye size={18} style={{ flex: 'none', marginTop: 2 }} />
        <Stack gap={4}>
          <Group gap="sm">
            <Text size="sm">
              <strong>Read-only.</strong> A schedule is Cartographer&rsquo;s to change; this page shows its
              consequence.
            </Text>
            <TownCrierLink path={path} />
          </Group>
          {limitation && <Text size="xs" c="dimmed">{limitation}</Text>}
        </Stack>
      </Group>
    </>
  )
}

function Forecast({
  data, selectedUtc, onSelect, detailRef,
}: {
  data: ScheduleLoad
  selectedUtc: string | null
  onSelect: (startUtc: string) => void
  detailRef: RefObject<HTMLDivElement | null>
}) {
  const seriesByKey = new Map(data.series.map((s) => [s.seriesKey, s]))
  const firstListed = firstListedBySeries(data.buckets)
  const worstBucket = data.buckets.find((b) => sameInstant(b.startUtc, data.summary.worstVerdictFirstAt)) ?? null
  const selected = data.buckets.find((b) => sameInstant(b.startUtc, selectedUtc))
    ?? worstBucket
    ?? data.buckets[0]
    ?? null
  const example = workerZoneExample(data.series, firstListed, data.workerZone.ianaId)

  const selectFromTable = (startUtc: string) => {
    onSelect(startUtc)
    detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <>
      <Banners banners={data.banners} />

      <VerdictBlock data={data} worstBucket={worstBucket} />

      <SectionCard
        title="The forecast, quarter-hour by quarter-hour"
        subtitle={`${plural(data.buckets.length, 'quarter-hour')} over ${windowLabel(data.hours)}. Select one to see what is in it.`}
      >
        <Stack gap="md">
          <ClockStrip display={data.displayZone} worker={data.workerZone} example={example} />
          <LoadTimeline
            buckets={data.buckets}
            selectedUtc={selected?.startUtc ?? null}
            onSelect={onSelect}
          />
          <HairlineDivider />
          <TimelineLegend counts={data.summary.bucketsByVerdict} />
        </Stack>
      </SectionCard>

      <div ref={detailRef} style={{ scrollMarginTop: 72 }}>
        {selected && (
          <BucketDetail
            bucket={selected}
            seriesByKey={seriesByKey}
            liveSlots={data.capacity.liveSlots}
            bucketSeconds={data.bucketSeconds}
            workerIanaId={data.workerZone.ianaId}
            townCrierPath={data.townCrier.path}
          />
        )}
      </div>

      <SectionCard
        title="Every quarter-hour, as numbers"
        subtitle="The figures behind the timeline's colours. Select a row to open it above."
      >
        <QuarterHourTable
          buckets={data.buckets}
          selectedUtc={selected?.startUtc ?? null}
          onSelect={selectFromTable}
        />
      </SectionCard>

      <ZonesPanel display={data.displayZone} worker={data.workerZone} example={example} />

      <CapacityPanel data={data} />

      <SectionCard
        title="What is scheduled"
        subtitle="Every report schedule and recurring job the forecast considered, placed or not, with what each run is expected to cost"
      >
        <SeriesTable
          series={data.series}
          firstListed={firstListed}
          workerIanaId={data.workerZone.ianaId}
          townCrierPath={data.townCrier.path}
        />
      </SectionCard>
    </>
  )
}

// ---------------------------------------------------------------------------
// Banners
// ---------------------------------------------------------------------------

function bannerIcon(severity: string): ReactNode {
  switch (severity) {
    case 'critical': return <IconCircleX size={16} />
    case 'serious': return <IconAlertOctagon size={16} />
    case 'warning': return <IconAlertTriangle size={16} />
    default: return <IconInfoCircle size={16} />
  }
}

/**
 * The server's headline states, in the server's order and words.
 *
 * Every banner is shown. Critical, serious and warning each get their own alert;
 * the info lines are grouped into one, so that eight context lines cannot bury
 * the one critical line above them. The text is the server's — it is written
 * from the same counts the rest of the page shows, and paraphrasing it here
 * would be a second place for it to drift.
 */
function Banners({ banners }: { banners: ScheduleLoadBanner[] }) {
  const { headline, context } = splitBanners(banners)
  if (banners.length === 0) return null

  return (
    <Stack gap="sm">
      {headline.map((banner) => (
        <Alert
          key={banner.code}
          icon={bannerIcon(banner.severity)}
          color={bannerColor(banner.severity)}
          variant="light"
          title={humanizeCode(banner.code)}
        >
          <Text size="sm">{banner.text}</Text>
        </Alert>
      ))}
      {context.length > 0 && (
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light" title="Also true of this forecast">
          <List size="sm" spacing={4}>
            {context.map((banner) => <List.Item key={banner.code}>{banner.text}</List.Item>)}
          </List>
        </Alert>
      )}
    </Stack>
  )
}

// ---------------------------------------------------------------------------
// The verdict
// ---------------------------------------------------------------------------

/**
 * "Will this break stuff" — answered, and then argued.
 *
 * The worst quarter-hour's verdict leads, with its meaning in words. If any
 * series was cut short, "a floor" sits beside it IN the headline (§6.4): a
 * truncated expansion under-reports, which is the dangerous direction, and a
 * banner lower down is too easy to read past. Then the scope sentence, the
 * headline counts, the tally per verdict, the counts behind the banners, and
 * the two lists — what is not modelled, and what is assumed — on screen.
 */
/**
 * "3 of them already contended on known demand alone": how many Indeterminate
 * quarter-hours the known demand alone already proves contended.
 *
 * Null for a count of 0, and null when the field is absent. An API older than the
 * field omits it, and "nothing to add" is the only honest reading of a count
 * nobody sent. It is never printed as "0 of them".
 */
function floorContendedNote(count: number | undefined): string | null {
  return typeof count === 'number' && count > 0
    ? `${exactNumber(count)} of them already contended on known demand alone`
    : null
}

function VerdictBlock({ data, worstBucket }: { data: ScheduleLoad; worstBucket: ScheduleLoadBucket | null }) {
  const s = data.summary
  const c = data.capacity
  const modelled = s.estimatedOccurrences + s.unknownOccurrences + s.floorCostOccurrences
  const notModelledStarts = Math.max(0, s.starts - modelled)
  const cut = s.truncatedSeriesCount + s.seriesNotExpanded + s.lookbackTruncatedSeriesCount
  const alreadyContended = floorContendedNote(s.indeterminateFloorContendedCount)
  // The server ranks an Indeterminate quarter-hour whose known demand already
  // contends ahead of an earlier one that proves nothing, so "first at" would
  // misread it: an earlier Indeterminate quarter-hour can exist.
  const worstIsFloorContended = s.worstVerdict === 'Indeterminate' && worstBucket?.floorVerdict === 'Contended'

  return (
    <SectionCard
      title="Will this break anything?"
      subtitle={`The worst quarter-hour in the next ${windowLabel(data.hours)}, and every figure the answer rests on`}
    >
      <Stack gap="md">
        <Stack gap={6}>
          <Group gap="sm" align="center">
            <Text size="sm" fw={600}>Worst quarter-hour</Text>
            <VerdictBadge verdict={s.worstVerdict} />
            {worstBucket && (
              <Text size="sm">
                {worstIsFloorContended ? 'first already contended on known demand at' : 'first at'}{' '}
                {atLabel(worstBucket.start)}
              </Text>
            )}
            {s.truncationFloor && (
              <Badge color="orange" variant="light">
                A floor — {plural(cut, 'series', 'series')} cut short, later firings missing
              </Badge>
            )}
          </Group>
          <Text size="sm">{verdictMeaning(s.worstVerdict)}</Text>
          {s.worstVerdict === 'Indeterminate' && worstBucket && (
            <Group gap={6}>
              <Text size="sm">There, the known demand alone:</Text>
              <VerdictBadge verdict={worstBucket.floorVerdict} />
              {worstBucket.floorVerdict == null && (
                <Text size="sm" c="dimmed">does not contend by itself, which is not the same as clear.</Text>
              )}
            </Group>
          )}
        </Stack>

        <Paper p="sm" withBorder style={{ backgroundColor: 'var(--color-bg-surface)' }}>
          <Text size="sm"><strong>Scope.</strong> {data.scope}</Text>
        </Paper>

        <div className="lh-overview-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <StatTile
            label="Starting in the window"
            value={exactNumber(s.starts)}
            sublabel={
              `${exactNumber(s.estimatedOccurrences)} estimated · ${exactNumber(s.unknownOccurrences)} unknown · `
              + `${exactNumber(s.floorCostOccurrences)} at a floor`
              + (notModelledStarts > 0 ? ` · ${exactNumber(notModelledStarts)} already delivered or refused` : '')
            }
          />
          {/*
            A share over nothing modelled is not 0%, it is unmeasured — and the
            server withholds it as null for exactly that case. Printing "0%" would
            read as "fully estimated" on an empty window.
          */}
          <StatTile
            label="With no cost estimate"
            value={s.unestimatedShare == null ? NOT_MEASURED : ratioPercent(s.unestimatedShare)}
            sublabel={
              s.unestimatedShare == null
                ? 'Nothing modelled to estimate'
                : s.mostlyUnestimated
                  ? 'Built mostly from unknowns'
                  : 'of the occurrences the model runs'
            }
          />
          <StatTile
            label="Live slots"
            value={countLabel(c.liveSlots)}
            sublabel={
              c.liveSlots == null
                ? (c.unmeasuredReason ?? 'Unmeasured — never read as zero slots')
                : `${plural(c.liveHostCount, 'host')}, measured ${agoLabel(c.observedAt)}`
            }
          />
          <StatTile
            label="Busy at the last heartbeat"
            value={countLabel(c.busyWorkerSlots)}
            sublabel={`queued ${countLabel(c.enqueuedJobCount)} · waiting to retry ${countLabel(c.scheduledJobCount)}`}
          />
        </div>

        <Stack gap={6}>
          <Text size="sm" fw={600}>Quarter-hours by verdict</Text>
          <Group gap="md">
            {VERDICT_ORDER.map((verdict) => (
              <Group key={verdict} gap={6}>
                <VerdictBadge verdict={verdict} />
                <Text size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {exactNumber(s.bucketsByVerdict[verdict] ?? 0)}
                </Text>
                {/*
                  Beside the Indeterminate count, never subtracted from it: these
                  quarter-hours ARE Indeterminate, and the tally sums to the
                  timeline. The note only stops the tally hiding contention the
                  known demand already proves.
                */}
                {verdict === 'Indeterminate' && alreadyContended && (
                  <Text size="sm" c="orange">— {alreadyContended}</Text>
                )}
              </Group>
            ))}
          </Group>
        </Stack>

        <HairlineDivider />

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          <Stack gap={6}>
            <Text size="sm" fw={600}>Behind the verdict</Text>
            <MetaList
              items={[
                { label: 'Series considered', value: exactNumber(s.seriesCount) },
                {
                  label: 'Cannot be placed',
                  value: `${exactNumber(s.unplaceableSeriesCount)} — ${exactNumber(s.unplaceableWillFireCount)} fire `
                    + `anyway at an hour this page cannot compute; ${exactNumber(s.unplaceableProbablyNotFiringCount)} `
                    + 'probably do not fire',
                },
                {
                  label: 'Cut short',
                  value: `${exactNumber(s.truncatedSeriesCount)} in the window · ${exactNumber(s.seriesNotExpanded)} `
                    + `not expanded · ${exactNumber(s.lookbackTruncatedSeriesCount)} in the lookback`,
                },
                { label: 'Run requests not yet picked up', value: exactNumber(s.pendingTriggerCount) },
                { label: 'Runs in flight now', value: exactNumber(s.inFlightRunCount) },
                {
                  label: 'Carried in from before the window',
                  value: slotSecondsLabel(s.seededBacklogSlotSeconds),
                },
                { label: 'Counted but not listed', value: exactNumber(s.itemsOmitted) },
                {
                  label: 'Simulated',
                  value: `In ${exactNumber(data.bucketSeconds)}-second buckets, from ${data.lookbackHours} hours `
                    + 'before the window, so a backlog left by earlier demand is carried in',
                },
              ]}
            />
          </Stack>
          <Stack gap="md">
            <Stack gap={6}>
              <Text size="sm" fw={600}>Not modelled — each can only add load</Text>
              <List size="sm" spacing={4}>
                {data.notModelled.map((line) => <List.Item key={line}>{line}</List.Item>)}
              </List>
            </Stack>
            <Stack gap={6}>
              <Text size="sm" fw={600}>Assumed, not measured</Text>
              <List size="sm" spacing={4}>
                {data.assumptions.map((line) => <List.Item key={line}>{line}</List.Item>)}
              </List>
            </Stack>
          </Stack>
        </SimpleGrid>
      </Stack>
    </SectionCard>
  )
}

// ---------------------------------------------------------------------------
// Zones
// ---------------------------------------------------------------------------

type ZoneExample = ReturnType<typeof workerZoneExample>

/** The worked example, or the rule when there is no example to hand. */
function zoneSentence(display: ScheduleLoadDisplayZone, worker: ScheduleLoadWorkerZone, example: ZoneExample): string {
  if (!worker.isPublished || worker.problem != null) {
    return 'Until Automaton publishes a zone this page can resolve, no worker job can be placed here and every '
      + 'quarter-hour reads Indeterminate. That is the correct reading, not a fault in the page.'
  }
  if (example) {
    return `${example.name} (${example.cron}) fires next at ${example.workerTime} on Automaton's clock, which is `
      + `${example.displayTime} on yours.`
  }
  if (worker.offsetAtWindowStartMinutes === display.offsetAtWindowStartMinutes) {
    return 'Automaton’s zone has your offset at the window start, so a job’s registered time reads the same '
      + 'here — until one of the two zones changes offset without the other.'
  }
  return `Automaton's clock is ${offsetLabel(worker.offsetAtWindowStartMinutes)} and yours is `
    + `${offsetLabel(display.offsetAtWindowStartMinutes)}, so a job registered for an hour there fires at a different `
    + 'hour here.'
}

/**
 * The two clocks, beside the timeline they govern.
 *
 * Fact 2 is the reason this strip exists: in production the worker schedules in
 * UTC, so the "02:30" maintenance chain sits in a Pacific evening, and an
 * operator reading the timeline will be surprised by it. The strip names both
 * zones and gives one real job from this window on both clocks.
 */
function ClockStrip({
  display, worker, example,
}: {
  display: ScheduleLoadDisplayZone
  worker: ScheduleLoadWorkerZone
  example: ZoneExample
}) {
  return (
    <Paper p="sm" withBorder style={{ backgroundColor: 'var(--color-bg-surface)' }}>
      <Stack gap={6}>
        <Group gap="xl" align="flex-start">
          <Stack gap={0}>
            <Text size="xs" c="dimmed">This timeline is drawn in</Text>
            <Text size="sm" fw={600}>
              <Code>{display.ianaId}</Code> {offsetLabel(display.offsetAtWindowStartMinutes)}
              {display.offsetChangesInWindow && ` → ${offsetLabel(display.offsetAtWindowEndMinutes)}`}
            </Text>
          </Stack>
          <Stack gap={0}>
            <Text size="xs" c="dimmed">Automaton schedules its jobs in</Text>
            <Text size="sm" fw={600}>
              {worker.zoneId ? <Code>{worker.zoneId}</Code> : NOT_MEASURED}{' '}
              {worker.offsetAtWindowStartMinutes != null && offsetLabel(worker.offsetAtWindowStartMinutes)}
            </Text>
          </Stack>
        </Group>
        <Text size="sm">{zoneSentence(display, worker, example)}</Text>
        {display.offsetChangesInWindow && (
          <Text size="xs" c="dimmed">
            Your zone changes offset inside this window. Each row of the timeline is one date at one offset, so a
            repeated hour gets its own row rather than being drawn over the first.
          </Text>
        )}
      </Stack>
    </Paper>
  )
}

/** Both zones in full: what was requested, what resolved, and why the worker's is published. */
function ZonesPanel({
  display, worker, example,
}: {
  display: ScheduleLoadDisplayZone
  worker: ScheduleLoadWorkerZone
  example: ZoneExample
}) {
  const problem = workerZoneProblemText(worker.problem)

  return (
    <SectionCard
      title="Which clock this is"
      subtitle="Every time on this page is on your clock. Report schedules carry their own zones; Automaton registers its jobs on its own."
    >
      <Stack gap="md">
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          <Stack gap={6}>
            <Text size="sm" fw={600}>This page — your browser&rsquo;s zone</Text>
            <MetaList
              items={[
                { label: 'Drawn in', value: display.ianaId, mono: true },
                ...(display.requested !== display.ianaId
                  ? [{ label: 'Your browser reported', value: display.requested, mono: true }]
                  : []),
                ...(display.resolvedId !== display.ianaId
                  ? [{ label: 'The API resolved it as', value: display.resolvedId, mono: true }]
                  : []),
                { label: 'Offset at the window start', value: offsetLabel(display.offsetAtWindowStartMinutes) },
                { label: 'Offset at the window end', value: offsetLabel(display.offsetAtWindowEndMinutes) },
                {
                  label: 'Offset changes in the window',
                  value: display.transitions.length === 0 ? 'None' : (
                    <Stack gap={2}>
                      {display.transitions.map((t) => (
                        <span key={t.atUtc}>
                          {transitionLabel(t.atUtc)}: {offsetLabel(t.offsetBeforeMinutes)} →{' '}
                          {offsetLabel(t.offsetAfterMinutes)}
                        </span>
                      ))}
                    </Stack>
                  ),
                },
              ]}
            />
          </Stack>
          <Stack gap={6}>
            <Text size="sm" fw={600}>Automaton — the zone it schedules jobs in</Text>
            {problem && (
              <Alert icon={<IconClockQuestion size={16} />} color="orange" variant="light">
                <Text size="sm">{problem}</Text>
              </Alert>
            )}
            <MetaList
              items={[
                { label: 'As published', value: worker.zoneId ?? NOT_MEASURED, mono: true },
                { label: 'IANA form', value: worker.ianaId ?? NOT_MEASURED, mono: true },
                { label: 'Offset at the window start', value: offsetLabel(worker.offsetAtWindowStartMinutes) },
                { label: 'Catalogue published by', value: worker.publishedByHost ?? NOT_MEASURED, mono: true },
                { label: 'Catalogue published', value: whenLabel(worker.cataloguePublishedAt) },
              ]}
            />
            {worker.otherLiveHosts.length > 0 && (
              <Text size="xs" c="orange">
                Also live: {worker.otherLiveHosts.join(', ')}. Each worker host re-registers the recurring jobs in
                its own zone when it starts, so some jobs may fire in a zone other than the one shown.
              </Text>
            )}
          </Stack>
        </SimpleGrid>

        <HairlineDivider />

        <Text size="sm">{zoneSentence(display, worker, example)}</Text>
        <Text size="xs" c="dimmed">
          <strong>Why Automaton&rsquo;s zone is published rather than assumed.</strong> Every recurring job is
          registered in the worker host&rsquo;s own local zone. In Azure the worker is a Linux App Service and no
          deployment file sets <Code>WEBSITE_TIME_ZONE</Code> or <Code>TZ</Code>, so there it schedules in UTC —
          which puts a maintenance chain registered for 02:30–04:15 at 19:30–21:15 Pacific daylight time the
          previous evening, where evening report schedules cluster. On a Windows developer machine it schedules in
          that machine&rsquo;s zone. This API cannot know the worker&rsquo;s zone from its own, so Automaton
          publishes it with the job catalogue. Report schedules are different: each carries its own zone —
          &ldquo;where the accountant is&rdquo; — and the page converts each one.
        </Text>
      </Stack>
    </SectionCard>
  )
}

/** "09:00 UTC, Sun 1 Nov" — a transition instant, read from its UTC string. */
function transitionLabel(atUtc: string): string {
  const wall = wallClock(atUtc)
  return wall ? `${wall.time} UTC, ${dayLabel(wall.date)}` : NOT_MEASURED
}

// ---------------------------------------------------------------------------
// Capacity and the present
// ---------------------------------------------------------------------------

/**
 * The capacity axis — which IS the worker fleet, hence the rail placing this
 * page beside Workers — and the work already holding or waiting for a slot.
 *
 * The degraded states are read in a fixed order, never-reported FIRST (build
 * plan #1): the never-reported response has `isProjectionStale: false`, so a
 * page asking about staleness first would present no measurement as a fresh one.
 */
function CapacityPanel({ data }: { data: ScheduleLoad }) {
  const navigate = useNavigate()
  const c = data.capacity

  return (
    <SectionCard
      title="Capacity, and what is already running"
      subtitle="Slots measured at the last heartbeat and assumed constant across the whole window"
      action={<SectionAction onClick={() => navigate('/workers')}>The fleet in full →</SectionAction>}
    >
      <Stack gap="md">
        <CapacityState capacity={c} />

        <MetaList
          items={[
            { label: 'Live slots', value: countLabel(c.liveSlots) },
            { label: 'Hosts counted', value: `${exactNumber(c.liveHostCount)} — one process per host, the newest` },
            { label: 'Busy slots at the heartbeat', value: countLabel(c.busyWorkerSlots) },
            { label: 'Jobs queued at the heartbeat', value: c.enqueuedJobCount == null ? 'Not measured' : exactNumber(c.enqueuedJobCount) },
            {
              label: 'Waiting in Hangfire’s delayed set (retries included)',
              value: c.scheduledJobCount == null ? 'Not measured' : exactNumber(c.scheduledJobCount),
            },
            { label: 'Busy slots the model cannot explain', value: countLabel(c.unexplainedBusySlots) },
            { label: 'Queued jobs the model cannot explain', value: countLabel(c.unexplainedQueuedJobs) },
            { label: 'Last heartbeat', value: whenLabel(c.observedAt) },
          ]}
        />

        {(c.overlappingIncarnations || c.queuesHeterogeneous) && (
          <Stack gap={4}>
            {c.overlappingIncarnations && (
              <Text size="xs" c="orange">
                A host has more than one live process; only the newest was counted, which under-counts a genuine
                second process.
              </Text>
            )}
            {c.queuesHeterogeneous && (
              <Text size="xs" c="orange">
                Not every live server listens to etl, reports and default, so the pooled slot count may overstate
                what one queue can use.
              </Text>
            )}
          </Stack>
        )}

        {c.hosts.length > 0 && (
          <Table.ScrollContainer minWidth={720}>
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Host</Table.Th>
                  <Table.Th ta="right">Slots</Table.Th>
                  <Table.Th ta="right">Busy</Table.Th>
                  <Table.Th>Queues</Table.Th>
                  <Table.Th ta="right">Live processes</Table.Th>
                  <Table.Th>Started</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {c.hosts.map((host) => (
                  <Table.Tr key={host.serverId}>
                    <Table.Td>
                      <Text size="sm" ff="monospace">{host.hostName}</Text>
                      <Text size="xs" c="dimmed" style={{ overflowWrap: 'anywhere' }}>{host.serverId}</Text>
                    </Table.Td>
                    <Table.Td ta="right">{exactNumber(host.workerCount)}</Table.Td>
                    <Table.Td ta="right">{exactNumber(host.busyWorkerCount)}</Table.Td>
                    <Table.Td>{host.queues.length > 0 ? host.queues.join(', ') : 'None reported'}</Table.Td>
                    <Table.Td ta="right">{exactNumber(host.liveIncarnationCount)}</Table.Td>
                    <Table.Td>{whenLabel(host.startedAt)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}

        {/*
          The hand-off goes to where the halt is shown in full. It is not a
          "Clear it" control: the clear-halt control is held until its endpoint
          pins the Bearer scheme (doc 17, amendment A4), and this page is
          read-only (S1).
        */}
        {data.halt && (
          <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light" title="ETL is halted">
            <Stack gap={6} align="flex-start">
              <Text size="sm">
                {humanizeCode(data.halt.reason)}, run <Code>{data.halt.runKey}</Code>, since{' '}
                {whenLabel(data.halt.haltedAt)}. Warehouse and entity occurrences will refuse to start and are not
                costed.
              </Text>
              <Button variant="subtle" size="compact-sm" onClick={() => navigate('/jobs')}>
                See the halt on the Scheduler →
              </Button>
            </Stack>
          </Alert>
        )}

        <HairlineDivider />
        <InFlight data={data} />
        <CarriedIn data={data} />
      </Stack>
    </SectionCard>
  )
}

function CapacityState({ capacity }: { capacity: ScheduleLoadCapacity }) {
  if (capacity.hasNeverReported) {
    return (
      <Alert icon={<IconServerOff size={16} />} color="gray" variant="light" title="No worker has ever reported">
        <Text size="sm">
          Capacity is unmeasured — not zero slots. Every demand figure is still shown; no quarter-hour is judged
          against a slot count, because a forecast against &ldquo;0 slots&rdquo; would call every occurrence a
          collision.
        </Text>
      </Alert>
    )
  }
  if (capacity.isProjectionStale) {
    return (
      <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light" title="The worker projection is stale">
        <Text size="sm">
          {capacity.unmeasuredReason ?? 'Automaton stopped refreshing the heartbeat projection.'} Capacity is
          treated as unmeasured, so the verdicts are too.
        </Text>
      </Alert>
    )
  }
  if (capacity.noLiveWorker) {
    return (
      <Alert icon={<IconServerOff size={16} />} color="red" variant="light" title="No live worker">
        <Text size="sm">Nothing scheduled will run until a worker starts.</Text>
      </Alert>
    )
  }
  if (capacity.liveSlots == null) {
    return (
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light" title="Capacity is unmeasured">
        <Text size="sm">{capacity.unmeasuredReason ?? 'The server did not say why.'}</Text>
      </Alert>
    )
  }
  return null
}

/** Work holding or waiting for a slot now that the schedule model did not produce. */
function InFlight({ data }: { data: ScheduleLoad }) {
  const rows = data.inFlight

  if (rows.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        {data.capacity.liveSlots == null
          ? 'No ETL run or comb pass is recorded as running. Busy slots cannot be compared with the model while '
            + 'capacity is unmeasured.'
          : 'Nothing is running or waiting now beyond what the schedule model itself accounts for.'}
      </Text>
    )
  }

  return (
    <Stack gap="xs">
      <Text size="sm" fw={600}>Already running or waiting — {plural(rows.length, 'row')}</Text>
      <Table.ScrollContainer minWidth={900}>
        <Table striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>What</Table.Th>
              <Table.Th ta="right">Count</Table.Th>
              <Table.Th>Started</Table.Th>
              <Table.Th>Planning end</Table.Th>
              <Table.Th>Keeps later quarter-hours doubtful until</Table.Th>
              <Table.Th>Detail</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row, i) => (
              <Table.Tr key={`${row.kind}|${row.runKey ?? row.label}|${i}`}>
                <Table.Td>
                  <Text size="sm" fw={500}>{row.label}</Text>
                  <Text size="xs" c="dimmed">{inFlightKindLabel(row.kind)}{row.runKey ? ` · ${row.runKey}` : ''}</Text>
                </Table.Td>
                <Table.Td ta="right">{exactNumber(row.count)}</Table.Td>
                {/*
                  whenLabel, not atLabel: these instants are served in UTC with no
                  display-zone rendering, and reading their wall clock off the
                  string would print a UTC time among Pacific ones. The browser's
                  conversion is exact here — the offset is explicit on the wire and
                  the browser's zone is the zone the forecast was requested in.
                */}
                <Table.Td style={{ whiteSpace: 'nowrap' }}>
                  <Text size="sm">{whenLabel(row.startedAt)}</Text>
                  {row.elapsedSeconds != null && (
                    <Text size="xs" c="dimmed">{durationLabel(row.elapsedSeconds)} so far</Text>
                  )}
                </Table.Td>
                <Table.Td style={{ whiteSpace: 'nowrap' }}>
                  {row.endUnknown ? 'Unknown' : whenLabel(row.planningEndsAt)}
                </Table.Td>
                <Table.Td style={{ whiteSpace: 'nowrap' }}>{whenLabel(row.uncertainUntil)}</Table.Td>
                <Table.Td><Text size="xs">{row.detail}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Stack>
  )
}

/**
 * Modelled work from before the window that is still running or waiting when
 * it opens — the inputs behind the first quarter-hours' seeded load. Without
 * this a 14:45 burst viewed at 15:07 would put occupied slot-seconds into 15:00
 * that no item listed there accounts for.
 */
function CarriedIn({ data }: { data: ScheduleLoad }) {
  const rows = data.carriedIn
  if (rows.length === 0) {
    return data.capacity.liveSlots == null ? null : (
      <Text size="sm" c="dimmed">No modelled work from before the window is still running or waiting when it opens.</Text>
    )
  }

  return (
    <Stack gap="xs">
      <Text size="sm" fw={600}>
        Carried in from before the window — {slotSecondsLabel(data.summary.seededBacklogSlotSeconds)} of it inside
        the window
      </Text>
      <Table.ScrollContainer minWidth={760}>
        <Table striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>What</Table.Th>
              <Table.Th ta="right">Running at the start</Table.Th>
              <Table.Th ta="right">Waiting at the start</Table.Th>
              <Table.Th ta="right">Occupies inside the window</Table.Th>
              <Table.Th>Earliest scheduled</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={row.seriesKey}>
                <Table.Td>
                  <Text size="sm" fw={500}>{row.name}</Text>
                  <Text size="xs" c="dimmed">{kindLabel(row.kind)}</Text>
                </Table.Td>
                <Table.Td ta="right">{exactNumber(row.runningAtWindowStart)}</Table.Td>
                <Table.Td ta="right">{exactNumber(row.waitingAtWindowStart)}</Table.Td>
                <Table.Td ta="right">{slotSecondsLabel(row.planningSlotSecondsInWindow)}</Table.Td>
                <Table.Td style={{ whiteSpace: 'nowrap' }}>{whenLabel(row.earliestScheduledAt)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Stack>
  )
}

// ---------------------------------------------------------------------------
// States with no forecast
// ---------------------------------------------------------------------------

/** The browser named no zone, and the page will not guess one. */
function NoBrowserZone() {
  return (
    <EmptyState
      icon={<IconCalendarOff size={28} stroke={1.5} />}
      title="Your browser did not report a time zone"
      message={
        'The forecast has to be drawn in a named zone, and this page will not assume one: a forecast drawn in a '
        + 'zone nobody asked for puts every spike in the wrong hour, and UTC is not a safe default for that reason. '
        + 'Nothing was requested. A browser with time-zone reporting enabled will show the forecast.'
      }
    />
  )
}

/**
 * The request failed. Nothing is known — including whether anything collides —
 * and the copy says so rather than rendering an empty forecast, which on this
 * page would read as "nothing is scheduled" or, worse, "all clear".
 *
 * Told apart by status (build plan #3), because each has a different next step.
 * Before, four causes landed as one paragraph. A 403 never reaches here: it is
 * the restricted state, not a failure.
 */
function LoadError({ error }: { error: unknown }) {
  const summary = errorSummary(error)
  return (
    <EmptyState
      icon={<IconClockQuestion size={28} stroke={1.5} />}
      title="Could not load the forecast"
      message={
        'No forecast came back, so nothing is known here about any schedule — including whether anything '
        + `collides. ${loadErrorCause(errorKind(error))}`
      }
      action={summary ? <Code style={{ overflowWrap: 'anywhere' }}>{summary}</Code> : undefined}
    />
  )
}

/** The likely cause and the next step, per kind of failure. */
function loadErrorCause(kind: ErrorKind): string {
  switch (kind) {
    case 'bad-request':
      return 'The API refused the zone or the window it was sent. Its reason is shown below.'
    case 'unauthorized':
      return 'The API did not accept this session. Sign in again, or check the API key this build was given.'
    case 'not-found':
      return 'This API has no schedule-load endpoint. It may be older than the forecast (build plan #8).'
    case 'server':
      return 'The API failed while building it. On a fresh database, migration AddSchedulingZoneAndQueueBacklog may '
        + 'not have been applied: the forecast reads its new columns, and migrations here are applied by hand. The dev '
        + 'proxy also answers 500 when the API is simply not running.'
    case 'network':
      return 'The API could not be reached at all: it is not running, or the network or CORS refused the request.'
    default:
      return 'The reason the server gave, if any, is shown below.'
  }
}
